/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import apiClient from '../../../../services/api-client';
import { RootState } from '../../../../store/rootReducer';
import { productToCartPayload } from '../../utils/productToCartItem';

import { SubmitCartReducerAction } from '../cart.slice';
import { CartObject, CartAPIError, SubmitCartPayload, SubmitCartApiResponse } from '../cart.types';

export const checkoutV1 = createAsyncThunk<
  SubmitCartReducerAction,
  { cart: CartObject; accountNumber: string },
  { rejectValue: CartAPIError; getState: RootState }
>('cart/orders', async ({ cart }, { rejectWithValue }) => {
  try {
    if (cart.pickupStore?.inventoryLocationId && cart.items.length > 0) {
      const payload: SubmitCartPayload = {
        accountNumber: cart.accountNumber,
        storeLocationId: cart.pickupStore.inventoryLocationId,
        customerReference: cart.customerReference ?? '',
        notes: cart.notes ?? '',
        internalNotes: cart.internalNotes ?? '',
        email: cart.email ?? '',
        phone: cart.phone ?? '',

        items: cart.items.map((item) => productToCartPayload(item)),
      };

      const request = await apiClient.post<SubmitCartApiResponse, SubmitCartPayload>(
        `/orders`,
        payload,
      );
      return {
        offlineCartId: cart.offlineCartId,
        store: cart.pickupStore,
        data: request.data,
      };
    } else {
      return rejectWithValue(new AxiosError('No cart'));
    }
  } catch (err) {
    const error: AxiosError = err as AxiosError;
    if (!error.response) {
      throw err;
    }
    return rejectWithValue(error.response.data as CartAPIError);
  }
});
